import { useEffect, useRef, useState } from 'react';

/** How much of the remaining distance each element closes per frame. */
const DOT_FOLLOW = 0.45;
const GLOW_FOLLOW = 0.1;

/** Below this the lerp is finished for any purpose the eye can tell. */
const SETTLED = 0.05;

/** How long after the last mouse move the glow is still counted as moving. */
const MOVING_MS = 120;

/**
 * Anything the small cursor should open up over. `data-cursor="hover"` opts
 * in an element that is clickable without being any of these.
 */
const INTERACTIVE =
  'a, button, [role="button"], input, select, textarea, label, summary, [data-cursor="hover"]';

/**
 * The custom cursor and the light that follows it.
 *
 * Two fixed elements: a small white dot that sits almost exactly on the
 * pointer, and a large radial glow that trails it, so the page around the
 * mouse reads as softly lit rather than as carrying a disc. Both are moved
 * with `translate3d` from one `requestAnimationFrame` loop that lerps toward
 * the last known mouse position -- `mousemove` only records coordinates and
 * never touches the DOM -- and the loop parks itself once both have settled,
 * so an idle page costs nothing.
 *
 * Mounted only for a fine pointer without a reduced-motion preference: a touch
 * device has no cursor to replace, and the trailing motion is exactly what
 * that preference asks not to see. The stylesheet repeats both checks, so even
 * a stale match cannot leave a dot on a phone. The native cursor is hidden by
 * a class on `<html>` for the lifetime of the mount, and only there.
 *
 * Everything the eye sees change in size or intensity -- the hover expansion,
 * the moving/idle glow, the fade at the viewport edge -- is a CSS transition
 * on `transform` or `opacity`, toggled by class. Nothing lays out.
 */
export function CustomCursor() {
  const [enabled] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia?.('(pointer: fine)').matches &&
      !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
  );

  const dot = useRef(null);
  const glow = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    const dotEl = dot.current;
    const glowEl = glow.current;
    if (!dotEl || !glowEl) return undefined;

    document.documentElement.classList.add('wonder-cursor-on');

    const mouse = { x: 0, y: 0 };
    const dotPos = { x: 0, y: 0 };
    const glowPos = { x: 0, y: 0 };
    let frame = 0;
    let seen = false;
    let movingTimer = 0;

    const place = (element, { x, y }) => {
      element.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%)`;
    };

    const tick = () => {
      dotPos.x += (mouse.x - dotPos.x) * DOT_FOLLOW;
      dotPos.y += (mouse.y - dotPos.y) * DOT_FOLLOW;
      glowPos.x += (mouse.x - glowPos.x) * GLOW_FOLLOW;
      glowPos.y += (mouse.y - glowPos.y) * GLOW_FOLLOW;
      place(dotEl, dotPos);
      place(glowEl, glowPos);

      const remaining =
        Math.abs(mouse.x - dotPos.x) +
        Math.abs(mouse.y - dotPos.y) +
        Math.abs(mouse.x - glowPos.x) +
        Math.abs(mouse.y - glowPos.y);
      frame = remaining > SETTLED ? requestAnimationFrame(tick) : 0;
    };

    const wake = () => {
      if (!frame) frame = requestAnimationFrame(tick);
    };

    const show = () => {
      dotEl.classList.add('is-visible');
      glowEl.classList.add('is-visible');
    };

    const hide = () => {
      dotEl.classList.remove('is-visible');
      glowEl.classList.remove('is-visible');
    };

    const onMove = (event) => {
      mouse.x = event.clientX;
      mouse.y = event.clientY;
      // The first move is where the cursor appears, not where it arrives from:
      // snap both onto the pointer so nothing is seen sliding in from (0, 0).
      if (!seen) {
        seen = true;
        Object.assign(dotPos, mouse);
        Object.assign(glowPos, mouse);
        place(dotEl, dotPos);
        place(glowEl, glowPos);
        show();
      }
      glowEl.classList.add('is-moving');
      clearTimeout(movingTimer);
      movingTimer = setTimeout(() => glowEl.classList.remove('is-moving'), MOVING_MS);
      wake();
    };

    // `mouseout` with no `relatedTarget` is the pointer leaving the window;
    // every other `mouseout` is one element handing off to another.
    const onOut = (event) => {
      if (!event.relatedTarget) hide();
    };
    const onOver = (event) => {
      if (!event.relatedTarget && seen) show();
      dotEl.classList.toggle('is-hover', Boolean(event.target.closest?.(INTERACTIVE)));
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    document.addEventListener('mouseout', onOut);
    document.addEventListener('mouseover', onOver);

    return () => {
      window.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseout', onOut);
      document.removeEventListener('mouseover', onOver);
      cancelAnimationFrame(frame);
      clearTimeout(movingTimer);
      document.documentElement.classList.remove('wonder-cursor-on');
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <>
      <div ref={glow} aria-hidden="true" className="wonder-cursor-glow" />
      <div ref={dot} aria-hidden="true" className="wonder-cursor-dot">
        <span className="wonder-cursor-dot-core" />
      </div>
    </>
  );
}
